/* Minori · impostazioni.
 *
 * Sono righe di SOLA LETTURA, e non per pigrizia: il backend non ha alcun
 * endpoint di preferenze utente — nessun CRUD utenti, nessun cambio password,
 * un solo livello di accesso. Un interruttore che non scrive da nessuna parte
 * sarebbe una bugia più costosa di una riga di testo.
 *
 * Quello che si può mostrare, e che serve davvero, sono i valori con cui il
 * backend precompila i moduli e decide le soglie: le quattro catene con i loro
 * predefiniti di gas, e i market watcher che alimentano i segnali. Sono gli
 * stessi numeri che compariranno nel modulo di un tradebot DEX, e vederli
 * prima evita di scoprire dopo che il gas non basta.
 */

import { Avvertenza, Distintivo } from '../../componenti';
import { ContenitoreDati, ScheletroLista } from '../../componenti/Stati';
import { marketWatchers, tokens } from '../../api/risorse';
import { usaRichiesta } from '../../api/usaRichiesta';
import { dataOra, num } from '../../lib/formato';
import { GruppoVoci, Voce } from './Voce';
import './minori.css';

export function Preferenze() {
  const catene = usaRichiesta(() => tokens.catene(), []);
  const watcher = usaRichiesta(() => marketWatchers.elenco({ sort: ['id,ASC'] }), []);

  const listaWatcher = watcher.dati?.data ?? [];
  const attivi = listaWatcher.filter((w) => w.isActive).length;
  const base = import.meta.env.VITE_API_BASE_URL ?? '/api';

  return (
    <div className="sb-colonna">
      <Avvertenza livello="nota">
        Nessuna di queste voci si può cambiare da qui: il backend non espone impostazioni utente.
        Sono i valori che il server usa, mostrati per poterli verificare prima di configurare un
        bot.
      </Avvertenza>

      <GruppoVoci titolo="Ambiente">
        <Voce
          chiave="Indirizzo delle API"
          valore={base}
          nota="in sviluppo il proxy toglie il prefisso /api e gira su localhost:3000"
        />
        <Voce
          chiave="Livelli di accesso"
          valore="uno solo"
          nota="autenticato o no: nessun ruolo, nessun permesso, nessun cambio password"
        />
      </GruppoVoci>

      <ContenitoreDati
        stato={catene.stato}
        causaVuoto="Il backend non dichiara nessuna catena supportata."
        messaggioErrore={catene.errore?.message}
        onRiprova={catene.ricarica}
        scheletro={<ScheletroLista righe={3} />}
      >
        <div className="sb-colonna">
          {(catene.dati ?? []).map((c) => (
            <GruppoVoci key={c.chainId} titolo={`${c.name} · chain ${c.chainId}`}>
              <Voce chiave="Moneta di gas" valore={c.nativeSymbol} />
              <Voce
                chiave="Nodi RPC pubblici"
                valore={num(c.rpcs.length)}
                nota="un eventuale RPC privato resta sul server e non compare qui"
              />
              {c.gasDefaults ? (
                <>
                  <Voce
                    chiave="Limite di gas per scambio"
                    valore={num(c.gasDefaults.gasLimit)}
                    nota={`${num(c.gasDefaults.swapGasUnits)} unità stimate per uno swap`}
                  />
                  <Voce
                    chiave="Saldo minimo di gas"
                    valore={`${c.gasDefaults.minimumBaseCoinBalance} ${c.nativeSymbol}`}
                    nota={`copre ${num(c.gasDefaults.swapsForMinimum)} operazioni al prezzo del gas ${
                      c.gasDefaults.gasPriceSource === 'rpc' ? 'letto adesso dal nodo' : 'di riferimento'
                    }`}
                  />
                  <Voce
                    chiave="Ricarica consigliata"
                    valore={`${c.gasDefaults.rechargeQtyBaseCoinBalance} ${c.nativeSymbol}`}
                    nota={`copre ${num(c.gasDefaults.swapsForRecharge)} operazioni`}
                  />
                  <Voce
                    chiave="Costo di uno scambio"
                    valore={`${num(c.gasDefaults.costPerSwap, 6)} ${c.nativeSymbol}`}
                    nota={`con margine per i picchi: ${num(c.gasDefaults.costPerSwapWithMargin, 6)} (×${c.gasDefaults.spikeMultiplier}) · gas a ${num(c.gasDefaults.gasPriceGwei, 2)} gwei`}
                  />
                  <Voce
                    chiave="Riferimento misurato"
                    valore={dataOra(c.gasDefaults.referenceMeasuredAt)}
                    nota={c.gasDefaults.notes || undefined}
                  />
                </>
              ) : (
                <Voce
                  chiave="Predefiniti di gas"
                  valore="non calcolati"
                  nota="il modulo del tradebot andrà compilato a mano su questa catena"
                />
              )}
            </GruppoVoci>
          ))}
        </div>
      </ContenitoreDati>

      <ContenitoreDati
        stato={watcher.stato}
        causaVuoto="Nessun market watcher: senza, i bot non ricevono candele su cui decidere."
        messaggioErrore={watcher.errore?.message}
        onRiprova={watcher.ricarica}
        scheletro={<ScheletroLista righe={2} />}
      >
        <GruppoVoci titolo={`Market watcher · ${attivi} attivi su ${listaWatcher.length}`}>
          {listaWatcher.map((w) => (
            <Voce
              key={w.id}
              chiave={`${w.asset}/${w.currency}`}
              valore={
                <Distintivo tono={w.isActive ? 'neutro' : 'cautela'}>
                  {w.isActive ? 'attivo' : 'spento'}
                </Distintivo>
              }
              nota={w.exchange?.name ?? `exchange #${w.exchangeId}`}
            />
          ))}
        </GruppoVoci>
      </ContenitoreDati>
    </div>
  );
}
